import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Music, Download, Clock } from 'lucide-react';
import { theme } from '../styles/theme';

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Container = styled.div`
  ${theme.glassContainer.normal}
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 40px;
  text-align: center;
  font-family: ${theme.typography.fontFamily};
`;

const LoadingIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: ${theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 2s ease-in-out infinite;
  color: white;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-left: 8px;
`;

const Title = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.semibold};
  margin: 0 0 16px 0;
`;

const Message = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.lg};
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const ProgressSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0;
`;

const ProgressStep = styled.div<{ $completed: boolean; $current: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${props => 
    props.$completed ? theme.colors.primary :
    props.$current ? theme.colors.glass.backgroundStrong :
    theme.colors.glass.background
  };
  border: 1px solid ${props =>
    props.$completed ? theme.colors.secondary :
    props.$current ? theme.colors.glass.borderStrong :
    theme.colors.glass.border
  };
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.normal};
`;

const StepIcon = styled.div<{ $completed: boolean; $current: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props =>
    props.$completed || props.$current ? theme.colors.text.primary : theme.colors.text.muted
  };
`;

const StepText = styled.span<{ $completed: boolean; $current: boolean }>`
  color: ${props =>
    props.$completed || props.$current ? theme.colors.text.primary : theme.colors.text.muted
  };
  font-weight: ${props =>
    props.$current ? theme.typography.weights.semibold : theme.typography.weights.normal
  };
`;

interface RecordingLoadingPageProps {
  callId: string;
  onRecordingReady: (recordingUrl: string, callData: any) => void;
  onError: (error: string) => void;
}

export const RecordingLoadingPage: React.FC<RecordingLoadingPageProps> = ({
  callId,
  onRecordingReady,
  onError
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const steps = [
    { icon: <Clock size={16} />, text: 'Call completed successfully' },
    { icon: <Download size={16} />, text: 'Processing recording' },
    { icon: <Music size={16} />, text: 'Creating your recording' }
  ];

  useEffect(() => {
    const maxAttempts = 30; // 30 attempts over ~60 seconds
    const pollInterval = 2000; // Check every 2 seconds

    // Mark first step as completed immediately
    setCurrentStep(1);

    const pollRecording = async () => {
      if (attempts >= maxAttempts) {
        onError('Recording took too long to process. Please check back later.');
        return;
      }

      try {
        const response = await fetch(`/api/retell/get-recording?callId=${callId}`);
        const data = await response.json();

        if (data.available && data.recording_url) {
          // Recording is ready!
          setCurrentStep(3);
          setTimeout(() => {
            onRecordingReady(data.recording_url, data);
          }, 1000); // Show completion briefly before transitioning
        } else {
          // Still processing
          setCurrentStep(2);
          setAttempts(prev => prev + 1);
          setTimeout(pollRecording, pollInterval);
        }
      } catch (error) {
        console.error('Error fetching recording:', error);
        setAttempts(prev => prev + 1);
        
        if (attempts >= maxAttempts) {
          onError('Failed to fetch recording. Please try again later.');
        } else {
          setTimeout(pollRecording, pollInterval);
        }
      }
    };

    // Start polling after a brief delay
    const timeoutId = setTimeout(pollRecording, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [callId, onRecordingReady, onError, attempts]);

  return (
    <Container>
      <LoadingIcon>
        <Music size={32} />
        <Spinner />
      </LoadingIcon>
      
      <Title>Creating Your Recording</Title>
      <Message>
        We're preparing your personalized fantasy football expert recording. This usually takes 30-60 seconds.
      </Message>

      <ProgressSteps>
        {steps.map((step, index) => (
          <ProgressStep
            key={index}
            $completed={currentStep > index + 1}
            $current={currentStep === index + 1}
          >
            <StepIcon 
              $completed={currentStep > index + 1}
              $current={currentStep === index + 1}
            >
              {step.icon}
            </StepIcon>
            <StepText
              $completed={currentStep > index + 1}
              $current={currentStep === index + 1}
            >
              {step.text}
            </StepText>
          </ProgressStep>
        ))}
      </ProgressSteps>
    </Container>
  );
};
